
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GlobalSet } from "../../global/GlobalProvider";

/* ==========================================
   RM CROWN MARKET
   PREMIUM TEAM INVEST REPORT
   Dark Navy + Cyan + White
========================================== */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const DATE_FILTERS = [
  { key: "total", label: "All Time", icon: "fa-chart-line" },
  { key: "today", label: "Today", icon: "fa-calendar-day" },
  { key: "week", label: "This Week", icon: "fa-calendar-week" },
  { key: "month", label: "This Month", icon: "fa-calendar" },
  { key: "year", label: "This Year", icon: "fa-calendar-check" },
];

const formatLocalDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatMoney = (value) =>
  toNumber(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getStatusInfo = (status) => {
  switch (Number(status)) {
    case 0:
      return {
        text: "Inactive",
        type: "inactive",
        icon: "fa-clock",
      };

    case 1:
      return {
        text: "Active",
        type: "active",
        icon: "fa-circle-check",
      };

    case 2:
      return {
        text: "Refunded",
        type: "refunded",
        icon: "fa-rotate-left",
      };

    case 3:
      return {
        text: "Processing",
        type: "processing",
        icon: "fa-hourglass-half",
      };

    default:
      return {
        text: "Unknown",
        type: "unknown",
        icon: "fa-circle-info",
      };
  }
};

/* ==========================================
   SMALL REUSABLE COMPONENTS
========================================== */

const ReportIcon = ({ name, className = "" }) => (
  <i
    className={`fa-solid ${name} ${className}`}
    aria-hidden="true"
  />
);

const SummaryCard = ({
  title,
  value,
  icon,
  subtitle,
  featured = false,
}) => (
  <div className={`rcm-summary ${featured ? "rcm-summary-featured" : ""}`}>
    <div className="rcm-summary-top">
      <span className="rcm-summary-icon">
        <ReportIcon name={icon} />
      </span>
      <span className="rcm-summary-indicator" />
    </div>

    <div className="rcm-summary-value">{value}</div>
    <div className="rcm-summary-title">{title}</div>

    {subtitle && (
      <div className="rcm-summary-subtitle">{subtitle}</div>
    )}
  </div>
);

const TeamInvestReport = () => {
  const { CurrencySymbol } = GlobalSet();

  const [investData, setInvestData] = useState([]);
  const [totalInvest, setTotalInvest] = useState({});

  const [filterType, setFilterType] = useState("total");

  const [search, setSearch] = useState("");
  const [level, setLevel] = useState("");
  const [appliedLevel, setAppliedLevel] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const requestRef = useRef(null);
  const firstLoadRef = useRef(true);

  const currency = CurrencySymbol || "$";

  /* ==========================================
     FETCH DATA
     Preserves existing API and payload
  ========================================== */

  const fetchData = useCallback(
    async ({
      type = "total",
      sDate = null,
      eDate = null,
      selectedLevel = "",
    } = {}) => {
      const authToken = localStorage.getItem("auth_token");

      const selectedCurrency =
        localStorage.getItem("selectedCurrency") || "USD";

      if (!authToken) {
        setError("Authentication token not found.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (requestRef.current) {
        requestRef.current.abort();
      }

      const controller = new AbortController();
      requestRef.current = controller;

      const payload = {
        key_s: API_KEY,
        currency: selectedCurrency,
        level: selectedLevel || "",
        selete_date: type,
        filter: "all",
        start_date: formatLocalDate(sDate),
        to_date: formatLocalDate(eDate),
      };

      setError("");

      if (firstLoadRef.current) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const response = await axios.post(
          "/team-statement",
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
            signal: controller.signal,
          }
        );

        if (response.data?.status === "success") {
          const records = Array.isArray(response.data.data)
            ? response.data.data
            : [];

          setInvestData(records);

          setTotalInvest(
            response.data.total_invest &&
              typeof response.data.total_invest === "object"
              ? response.data.total_invest
              : {}
          );

          setTotalAmount(toNumber(response.data.total_amount));
        } else {
          setInvestData([]);
          setTotalInvest({});
          setTotalAmount(0);
          setError(response.data?.message || "Unable to load report.");
        }
      } catch (err) {
        if (
          err.name === "CanceledError" ||
          err.name === "AbortError" ||
          axios.isCancel(err)
        ) {
          return;
        }

        console.error("Team Invest Report Error:", err);

        setError("Unable to refresh report. Please try again.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
          firstLoadRef.current = false;
        }
      }
    },
    []
  );

  /* ==========================================
     INITIAL DATA LOAD
  ========================================== */

  useEffect(() => {
    fetchData({
      type: "total",
      selectedLevel: "",
    });

    return () => {
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchData]);

  /* ==========================================
     FILTER HANDLERS
  ========================================== */

  const handleFilterClick = (type) => {
    setFilterType(type);
    setPage(1);

    fetchData({
      type,
      selectedLevel: appliedLevel,
    });
  };

  const handleDateRangeFilter = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }

    if (startDate > endDate) {
      setError("Start date cannot be after end date.");
      return;
    }

    setError("");
    setFilterType("custom");
    setPage(1);

    fetchData({
      type: "custom",
      sDate: startDate,
      eDate: endDate,
      selectedLevel: appliedLevel,
    });
  };

  const handleLevelFilter = () => {
    setAppliedLevel(level);
    setPage(1);

    fetchData({
      type: filterType,
      sDate: filterType === "custom" ? startDate : null,
      eDate: filterType === "custom" ? endDate : null,
      selectedLevel: level,
    });
  };

  const handleRefresh = () => {
    fetchData({
      type: filterType,
      sDate: filterType === "custom" ? startDate : null,
      eDate: filterType === "custom" ? endDate : null,
      selectedLevel: appliedLevel,
    });
  };

  const handleReset = () => {
    setFilterType("total");
    setSearch("");
    setLevel("");
    setAppliedLevel("");
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    setError("");

    fetchData({
      type: "total",
      selectedLevel: "",
    });
  };

  /* ==========================================
     FAST CLIENT SIDE FILTERING
  ========================================== */

  const filteredData = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return investData.filter((item) => {
      if (!searchText) return true;

      return String(item.username || "")
        .toLowerCase()
        .includes(searchText);
    });
  }, [investData, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredData.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  /* ==========================================
     SUMMARY DATA
  ========================================== */

  const summary = useMemo(() => {
    const active = investData.filter(
      (item) => toNumber(item.total_invest) > 0
    ).length;

    const inactive = investData.length - active;

    const visibleAmount = filteredData.reduce(
      (sum, item) => sum + toNumber(item.fixt_amount),
      0
    );

    return {
      records: investData.length,
      active,
      inactive,
      filtered: filteredData.length,
      visibleAmount,
      totalAmount: toNumber(totalAmount),
    };
  }, [investData, filteredData, totalAmount]);

  const branches = useMemo(
    () => [
      {
        title: "Branch A",
        amount: toNumber(totalInvest.sumA),
        icon: "fa-code-branch",
      },
      {
        title: "Branch B",
        amount: toNumber(totalInvest.sumB),
        icon: "fa-code-branch",
      },
      {
        title: "Branch C",
        amount: toNumber(totalInvest.sumC),
        icon: "fa-code-branch",
      },
      {
        title: "Other Teams",
        amount: toNumber(totalInvest.team_level_others),
        icon: "fa-users",
      },
    ],
    [totalInvest]
  );

  const showBranchSummary = [
    "sumA",
    "sumB",
    "sumC",
    "team_level_others",
  ].some((key) => totalInvest[key] !== undefined);

  /* ==========================================
     RENDER
  ========================================== */

  return (
    <div className="rcm-report-root">
      <style>{`
        /* =====================================
           THEME
        ===================================== */

        .rcm-report-root {
          --rcm-bg: #00191D;
          --rcm-card: #03272D;
          --rcm-card-light: #06333A;
          --rcm-cyan: #16B8C7;
          --rcm-cyan-light: #5DE2EA;
          --rcm-white: #FFFFFF;
          --rcm-muted: #9DB8BC;
          --rcm-border: rgba(22,184,199,.19);
          --rcm-green: #39D5A2;
          --rcm-red: #FF777F;
          --rcm-yellow: #F3C56A;

          width: 100%;
          min-height: 100vh;
          margin: 0;
          padding: 14px 4px 35px;
          background: #00191D;
          color: #FFFFFF;
          font-family: Inter, system-ui, -apple-system,
            BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 14px;
          line-height: 1.5;
          overflow-x: hidden;
        }

        .rcm-report-root *,
        .rcm-report-root *::before,
        .rcm-report-root *::after {
          box-sizing: border-box;
        }

        .rcm-report-root button,
        .rcm-report-root input,
        .rcm-report-root select {
          font-family: inherit;
        }

        .rcm-report-container {
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          padding: 0 4px;
        }

        /* =====================================
           HEADER
        ===================================== */

        .rcm-report-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          padding: 20px 22px;
          margin-bottom: 16px;
          border: 1px solid var(--rcm-border);
          border-radius: 20px;
          background:
            linear-gradient(
              115deg,
              #06343B 0%,
              #03252A 55%,
              #00191D 100%
            );
          position: relative;
          overflow: hidden;
        }

        .rcm-report-header::after {
          content: "";
          position: absolute;
          right: -65px;
          top: -90px;
          width: 210px;
          height: 210px;
          border-radius: 50%;
          background: rgba(22,184,199,.08);
          pointer-events: none;
        }

        .rcm-header-left {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
          position: relative;
          z-index: 1;
        }

        .rcm-header-icon {
          width: 52px;
          height: 52px;
          flex-shrink: 0;
          border-radius: 15px;
          background: linear-gradient(
            135deg,
            #16B8C7,
            #087E8A
          );
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: #fff;
        }

        .rcm-header-eyebrow {
          font-size: 11px;
          letter-spacing: 1.5px;
          font-weight: 800;
          color: var(--rcm-cyan-light);
          text-transform: uppercase;
          margin-bottom: 3px;
        }

        .rcm-header-title {
          font-size: 23px;
          font-weight: 800;
          color: #fff;
          margin: 0;
          letter-spacing: -.5px;
        }

        .rcm-header-subtitle {
          color: var(--rcm-muted);
          font-size: 13px;
          margin-top: 3px;
        }

        .rcm-header-actions {
          display: flex;
          align-items: center;
          gap: 9px;
          position: relative;
          z-index: 1;
        }

        /* =====================================
           BUTTONS
        ===================================== */

        .rcm-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 41px;
          padding: 9px 15px;
          border-radius: 11px;
          border: 1px solid transparent;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition:
            background .18s ease,
            border-color .18s ease,
            transform .18s ease;
          white-space: nowrap;
          text-decoration: none;
        }

        .rcm-btn:hover {
          transform: translateY(-1px);
        }

        .rcm-btn:active {
          transform: translateY(0);
        }

        .rcm-btn-primary {
          background: linear-gradient(
            135deg,
            #20CAD7,
            #0796A5
          );
          color: #00191D;
          border-color: rgba(255,255,255,.12);
        }

        .rcm-btn-primary:hover {
          background: linear-gradient(
            135deg,
            #5DE2EA,
            #16B8C7
          );
          color: #00191D;
        }

        .rcm-btn-outline {
          background: rgba(255,255,255,.035);
          border-color: var(--rcm-border);
          color: #fff;
        }

        .rcm-btn-outline:hover {
          background: rgba(22,184,199,.1);
          color: #fff;
          border-color: var(--rcm-cyan);
        }

        .rcm-btn:focus-visible,
        .rcm-input:focus-visible,
        .rcm-select:focus-visible {
          outline: 2px solid var(--rcm-cyan-light);
          outline-offset: 2px;
        }

        /* =====================================
           SUMMARY
        ===================================== */

        .rcm-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0,1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .rcm-summary {
          padding: 17px;
          min-width: 0;
          border-radius: 17px;
          border: 1px solid var(--rcm-border);
          background:
            linear-gradient(
              145deg,
              #073138,
              #03252A 65%,
              #022126
            );
          transition:
            border-color .2s ease,
            transform .2s ease;
        }

        .rcm-summary:hover {
          border-color: rgba(22,184,199,.5);
          transform: translateY(-2px);
        }

        .rcm-summary-featured {
          background:
            linear-gradient(
              135deg,
              #0D6470,
              #06424B 55%,
              #03272D
            );
          border-color: rgba(22,184,199,.55);
        }

        .rcm-summary-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 15px;
        }

        .rcm-summary-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          color: var(--rcm-cyan-light);
          background: rgba(22,184,199,.12);
          font-size: 17px;
        }

        .rcm-summary-featured .rcm-summary-icon {
          background: rgba(255,255,255,.13);
          color: #fff;
        }

        .rcm-summary-indicator {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--rcm-cyan);
        }

        .rcm-summary-value {
          font-size: 23px;
          line-height: 1.25;
          font-weight: 800;
          color: #fff;
          overflow-wrap: anywhere;
          font-variant-numeric: tabular-nums;
        }

        .rcm-summary-title {
          font-size: 13px;
          font-weight: 700;
          color: #E7F5F6;
          margin-top: 5px;
        }

        .rcm-summary-subtitle {
          font-size: 11px;
          color: var(--rcm-muted);
          margin-top: 3px;
        }

        /* =====================================
           PANELS
        ===================================== */

        .rcm-panel {
          background: #03272D;
          border: 1px solid var(--rcm-border);
          border-radius: 18px;
          margin-bottom: 16px;
          overflow: hidden;
        }

        .rcm-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 17px 19px;
          border-bottom: 1px solid var(--rcm-border);
        }

        .rcm-panel-heading {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .rcm-panel-heading-icon {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: rgba(22,184,199,.12);
          color: var(--rcm-cyan-light);
        }

        .rcm-panel-title {
          font-size: 16px;
          font-weight: 800;
          margin: 0;
          color: #fff;
        }

        .rcm-panel-description {
          color: var(--rcm-muted);
          font-size: 12px;
          margin-top: 2px;
        }

        .rcm-panel-body {
          padding: 18px;
        }

        /* =====================================
           FILTERS
        ===================================== */

        .rcm-filter-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 14px;
        }

        .rcm-filter-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .rcm-filter-tab {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 39px;
          padding: 8px 13px;
          border: 1px solid var(--rcm-border);
          background: #001D22;
          color: #C8E0E3;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all .18s ease;
        }

        .rcm-filter-tab:hover {
          background: #0B4048;
          color: #fff;
        }

        .rcm-filter-tab.active {
          background: linear-gradient(
            135deg,
            #16B8C7,
            #0B91A0
          );
          color: #00191D;
          border-color: #16B8C7;
        }

        .rcm-date-group {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .rcm-date-item {
          width: 145px;
        }

        .rcm-date-item .react-datepicker-wrapper,
        .rcm-date-item .react-datepicker__input-container {
          display: block;
          width: 100%;
        }

        .rcm-input,
        .rcm-select {
          width: 100%;
          height: 42px;
          background: #001D22;
          border: 1px solid rgba(22,184,199,.25);
          border-radius: 10px;
          color: #fff;
          padding: 0 13px;
          font-size: 14px;
          outline: none;
          transition: border-color .18s ease;
        }

        .rcm-input:focus,
        .rcm-select:focus {
          border-color: var(--rcm-cyan);
        }

        .rcm-input::placeholder {
          color: #7D9CA1;
        }

        .rcm-select option {
          background: #03272D;
          color: #fff;
        }

        .rcm-filter-divider {
          height: 1px;
          background: var(--rcm-border);
          margin: 17px 0;
        }

        .rcm-filter-bottom {
          display: grid;
          grid-template-columns:
            minmax(130px, 1fr)
            minmax(220px, 2fr)
            auto;
          gap: 12px;
          align-items: end;
        }

        .rcm-field-label {
          display: block;
          margin-bottom: 7px;
          font-size: 12px;
          font-weight: 700;
          color: #B9D4D8;
        }

        .rcm-input-wrap {
          position: relative;
        }

        .rcm-input-wrap > i {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--rcm-cyan);
          pointer-events: none;
        }

        .rcm-input-wrap .rcm-input {
          padding-left: 38px;
        }

        .rcm-filter-actions {
          display: flex;
          gap: 7px;
        }

        /* =====================================
           BRANCH SUMMARY
        ===================================== */

        .rcm-branch-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0,1fr));
          gap: 10px;
        }

        .rcm-branch-card {
          padding: 15px;
          border-radius: 12px;
          border: 1px solid var(--rcm-border);
          background: #001D22;
          min-width: 0;
        }

        .rcm-branch-name {
          display: flex;
          align-items: center;
          gap: 7px;
          color: var(--rcm-muted);
          font-size: 12px;
          margin-bottom: 9px;
        }

        .rcm-branch-name i {
          color: var(--rcm-cyan);
        }

        .rcm-branch-amount {
          font-size: 18px;
          font-weight: 800;
          color: #fff;
          overflow-wrap: anywhere;
        }

        /* =====================================
           TABLE
        ===================================== */

        .rcm-table-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
        }

        .rcm-table-meta {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .rcm-record-count {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 11px;
          background: rgba(22,184,199,.12);
          color: #5DE2EA;
          border-radius: 9px;
          font-size: 12px;
          font-weight: 700;
        }

        .rcm-refreshing {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: var(--rcm-cyan-light);
          font-size: 12px;
        }

        .rcm-table-scroll {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .rcm-table {
          width: 100%;
          min-width: 850px;
          border-collapse: collapse;
          border-spacing: 0;
          margin: 0;
          background: transparent;
        }

        .rcm-table thead {
          background: #073941;
        }

        .rcm-table th {
          padding: 14px 13px;
          font-size: 12px;
          font-weight: 800;
          text-align: left;
          white-space: nowrap;
          color: #AEEAF0;
          border-bottom: 1px solid var(--rcm-border);
        }

        .rcm-table td {
          padding: 13px;
          font-size: 13px;
          color: #E6F3F4;
          border-bottom: 1px solid rgba(22,184,199,.09);
          vertical-align: middle;
          white-space: nowrap;
        }

        .rcm-table tbody tr {
          transition: background .15s ease;
        }

        .rcm-table tbody tr:hover {
          background: rgba(22,184,199,.055);
        }

        .rcm-table tbody tr:last-child td {
          border-bottom: none;
        }

        .rcm-username {
          display: flex;
          align-items: center;
          gap: 9px;
          font-weight: 700;
          color: #fff;
        }

        .rcm-avatar {
          width: 32px;
          height: 32px;
          border-radius: 9px;
          background: rgba(22,184,199,.14);
          color: var(--rcm-cyan-light);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 12px;
          flex-shrink: 0;
        }

        .rcm-amount {
          font-weight: 800;
          color: #5DE2EA;
          font-variant-numeric: tabular-nums;
        }

        .rcm-site {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 35px;
          padding: 5px 10px;
          border: 1px solid rgba(22,184,199,.25);
          border-radius: 7px;
          background: rgba(22,184,199,.07);
          color: #9CE6EC;
          font-size: 11px;
          font-weight: 800;
        }

        .rcm-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border-radius: 7px;
          padding: 6px 9px;
          font-size: 11px;
          font-weight: 700;
        }

        .rcm-badge-active {
          background: rgba(57,213,162,.11);
          color: #4BE1B1;
        }

        .rcm-badge-inactive {
          background: rgba(243,197,106,.11);
          color: #F3C56A;
        }

        .rcm-badge-refunded {
          background: rgba(255,119,127,.11);
          color: #FF9299;
        }

        .rcm-badge-processing {
          background: rgba(22,184,199,.12);
          color: #5DE2EA;
        }

        .rcm-badge-unknown {
          background: rgba(255,255,255,.07);
          color: #B5C9CC;
        }

        .rcm-empty {
          text-align: center;
          padding: 55px 15px;
          color: var(--rcm-muted);
        }

        .rcm-empty-icon {
          width: 60px;
          height: 60px;
          border-radius: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(22,184,199,.1);
          color: var(--rcm-cyan);
          margin: 0 auto 15px;
          font-size: 25px;
        }

        .rcm-empty-title {
          font-size: 16px;
          font-weight: 800;
          color: #fff;
          margin-bottom: 5px;
        }

        .rcm-empty-desc {
          font-size: 13px;
          color: var(--rcm-muted);
        }

        /* =====================================
           PAGINATION
        ===================================== */

        .rcm-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding: 14px 18px;
          border-top: 1px solid var(--rcm-border);
        }

        .rcm-pagination-info {
          color: var(--rcm-muted);
          font-size: 12px;
        }

        .rcm-pagination-controls {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .rcm-page-btn {
          height: 34px;
          min-width: 34px;
          border-radius: 8px;
          border: 1px solid var(--rcm-border);
          background: #001D22;
          color: #fff;
          cursor: pointer;
          transition: background .15s ease;
        }

        .rcm-page-btn:hover:not(:disabled) {
          background: #0A4149;
        }

        .rcm-page-btn:disabled {
          opacity: .35;
          cursor: not-allowed;
        }

        .rcm-page-current {
          color: var(--rcm-cyan-light);
          font-weight: 800;
          font-size: 12px;
          padding: 0 7px;
        }

        .rcm-page-size {
          width: 75px;
          height: 34px;
          font-size: 12px;
          padding: 0 8px;
        }

        /* =====================================
           ERROR
        ===================================== */

        .rcm-error {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 12px 15px;
          margin-bottom: 15px;
          border-radius: 11px;
          background: rgba(255,119,127,.08);
          border: 1px solid rgba(255,119,127,.25);
          color: #FFADB2;
          font-size: 13px;
        }

        .rcm-error-close {
          background: transparent;
          color: inherit;
          border: none;
          cursor: pointer;
          font-size: 16px;
        }

        /* =====================================
           DATEPICKER
        ===================================== */

        .react-datepicker {
          font-family: inherit;
          border: 1px solid #14616B;
          border-radius: 12px;
          background: #03272D;
          color: #fff;
          overflow: hidden;
        }

        .react-datepicker__header {
          background: #073941;
          border-bottom: 1px solid #14616B;
        }

        .react-datepicker__current-month,
        .react-datepicker__day-name,
        .react-datepicker__day {
          color: #fff;
        }

        .react-datepicker__day:hover {
          background: #0B6975;
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background: #16B8C7 !important;
          color: #00191D !important;
        }

        .react-datepicker__day--disabled {
          opacity: .3;
        }

        .react-datepicker__triangle {
          display: none;
        }

        /* =====================================
           ANIMATION
        ===================================== */

        @keyframes rcmFadeIn {
          from {
            opacity: 0;
            transform: translateY(7px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .rcm-report-header,
        .rcm-summary-grid,
        .rcm-panel {
          animation: rcmFadeIn .3s ease both;
        }

        /* =====================================
           RESPONSIVE
        ===================================== */

        @media (max-width: 1100px) {
          .rcm-summary-grid {
            grid-template-columns: repeat(2, minmax(0,1fr));
          }

          .rcm-filter-top {
            flex-direction: column;
          }

          .rcm-date-group {
            width: 100%;
          }

          .rcm-date-item {
            flex: 1;
            width: auto;
          }
        }

        @media (max-width: 768px) {
          .rcm-report-root {
            padding: 8px 4px 25px;
          }

          .rcm-report-container {
            padding: 0;
          }

          .rcm-report-header {
            padding: 14px;
            border-radius: 15px;
          }

          .rcm-header-icon {
            width: 43px;
            height: 43px;
            border-radius: 12px;
            font-size: 18px;
          }

          .rcm-header-title {
            font-size: 17px;
          }

          .rcm-header-eyebrow {
            font-size: 9px;
          }

          .rcm-header-subtitle {
            font-size: 11px;
          }

          .rcm-header-actions .rcm-btn {
            padding: 9px 11px;
            min-width: 40px;
          }

          .rcm-header-actions .rcm-btn-label {
            display: none;
          }

          .rcm-summary-grid {
            gap: 8px;
          }

          .rcm-summary {
            padding: 13px;
            border-radius: 13px;
          }

          .rcm-summary-value {
            font-size: 19px;
          }

          .rcm-summary-icon {
            width: 34px;
            height: 34px;
            font-size: 14px;
          }

          .rcm-panel {
            border-radius: 14px;
            margin-bottom: 12px;
          }

          .rcm-panel-header {
            padding: 13px;
          }

          .rcm-panel-body {
            padding: 12px;
          }

          .rcm-filter-tabs {
            display: grid;
            grid-template-columns: repeat(3, minmax(0,1fr));
            width: 100%;
          }

          .rcm-filter-tab {
            padding: 8px 6px;
            font-size: 11px;
          }

          .rcm-filter-bottom {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .rcm-filter-actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
          }

          .rcm-date-group {
            flex-wrap: wrap;
          }

          .rcm-date-item {
            flex: 1 1 calc(50% - 6px);
          }

          .rcm-date-group > .rcm-btn {
            width: 100%;
          }

          .rcm-branch-grid {
            grid-template-columns: repeat(2, minmax(0,1fr));
          }

          .rcm-branch-card {
            padding: 12px;
          }

          .rcm-branch-amount {
            font-size: 16px;
          }

          .rcm-table th,
          .rcm-table td {
            padding: 11px;
          }

          .rcm-pagination {
            padding: 12px;
          }
        }

        @media (max-width: 390px) {
          .rcm-header-title {
            font-size: 15px;
          }

          .rcm-summary-value {
            font-size: 17px;
          }

          .rcm-filter-tabs {
            grid-template-columns: repeat(2, minmax(0,1fr));
          }

        }

        @media (prefers-reduced-motion: reduce) {
          .rcm-report-root *,
          .rcm-report-root *::before,
          .rcm-report-root *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="rcm-report-container">

        {/* ===================================
            PAGE HEADER
        =================================== */}

        <header className="rcm-report-header">
          <div className="rcm-header-left">
            <div className="rcm-header-icon">
              <ReportIcon name="fa-users-line" />
            </div>

            <div>
              <div className="rcm-header-eyebrow">
                RM CROWN MARKET
              </div>

              <h1 className="rcm-header-title">
                Team Investment Report
              </h1>

              <div className="rcm-header-subtitle">
                Monitor your network investment activity
              </div>
            </div>
          </div>

          <div className="rcm-header-actions">
            <button
              type="button"
              className="rcm-btn rcm-btn-outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              title="Refresh Report"
            >
              <ReportIcon
                name={
                  refreshing
                    ? "fa-arrows-rotate fa-spin"
                    : "fa-arrows-rotate"
                }
              />
              <span className="rcm-btn-label">Refresh</span>
            </button>
          </div>
        </header>

        {/* ===================================
            ERROR MESSAGE
        =================================== */}

        {error && (
          <div className="rcm-error" role="alert">
            <span>
              <ReportIcon name="fa-circle-exclamation" />{" "}
              {error}
            </span>

            <button
              type="button"
              className="rcm-error-close"
              onClick={() => setError("")}
              aria-label="Dismiss error"
            >
              <ReportIcon name="fa-xmark" />
            </button>
          </div>
        )}

        {/* ===================================
            SUMMARY CARDS
        =================================== */}

        <section className="rcm-summary-grid">
          <SummaryCard
            title="Total Team Investment"
            value={`${currency}${formatMoney(summary.totalAmount)}`}
            subtitle="API reported investment"
            icon="fa-wallet"
            featured
          />

          <SummaryCard
            title="Total Records"
            value={summary.records.toLocaleString()}
            subtitle="Current report period"
            icon="fa-users"
          />

          <SummaryCard
            title="Active Members"
            value={summary.active.toLocaleString()}
            subtitle="Members with investment"
            icon="fa-user-check"
          />

          <SummaryCard
            title="Inactive Members"
            value={summary.inactive.toLocaleString()}
            subtitle="Members without investment"
            icon="fa-user-clock"
          />
        </section>

        {/* ===================================
            FILTER PANEL
        =================================== */}

        <section className="rcm-panel">
          <div className="rcm-panel-header">
            <div className="rcm-panel-heading">
              <div className="rcm-panel-heading-icon">
                <ReportIcon name="fa-sliders" />
              </div>

              <div>
                <h2 className="rcm-panel-title">
                  Report Filters
                </h2>
                <div className="rcm-panel-description">
                  Customize your investment report
                </div>
              </div>
            </div>

            <button
              type="button"
              className="rcm-btn rcm-btn-outline"
              onClick={handleReset}
            >
              <ReportIcon name="fa-rotate-left" />
              Reset
            </button>
          </div>

          <div className="rcm-panel-body">

            {/* QUICK DATE FILTER */}

            <div className="rcm-field-label">
              Quick Date Selection
            </div>

            <div className="rcm-filter-top">
              <div className="rcm-filter-tabs">
                {DATE_FILTERS.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={`rcm-filter-tab ${
                      filterType === filter.key ? "active" : ""
                    }`}
                    onClick={() => handleFilterClick(filter.key)}
                  >
                    <ReportIcon name={filter.icon} />
                    {filter.label}
                  </button>
                ))}
              </div>

              <div className="rcm-date-group">
                <div className="rcm-date-item">
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={endDate || undefined}
                    placeholderText="Start Date"
                    className="rcm-input"
                    dateFormat="dd MMM yyyy"
                    isClearable
                  />
                </div>

                <div className="rcm-date-item">
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate || undefined}
                    placeholderText="End Date"
                    className="rcm-input"
                    dateFormat="dd MMM yyyy"
                    isClearable
                  />
                </div>

                <button
                  type="button"
                  className="rcm-btn rcm-btn-primary"
                  onClick={handleDateRangeFilter}
                >
                  <ReportIcon name="fa-filter" />
                  Apply
                </button>
              </div>
            </div>

            <div className="rcm-filter-divider" />

            {/* LEVEL AND SEARCH */}

            <div className="rcm-filter-bottom">
              <div>
                <label className="rcm-field-label" htmlFor="rcm-level">
                  Generation Level
                </label>

                <div className="rcm-input-wrap">
                  <ReportIcon name="fa-layer-group" />

                  <input
                    id="rcm-level"
                    type="number"
                    min="1"
                    className="rcm-input"
                    placeholder="All Levels"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleLevelFilter();
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="rcm-field-label" htmlFor="rcm-search">
                  Search Username
                </label>

                <div className="rcm-input-wrap">
                  <ReportIcon name="fa-magnifying-glass" />

                  <input
                    id="rcm-search"
                    type="search"
                    className="rcm-input"
                    placeholder="Search team member..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="rcm-filter-actions">
                <button
                  type="button"
                  className="rcm-btn rcm-btn-primary"
                  onClick={handleLevelFilter}
                >
                  <ReportIcon name="fa-check" />
                  Apply Level
                </button>

                <button
                  type="button"
                  className="rcm-btn rcm-btn-outline"
                  onClick={handleReset}
                >
                  <ReportIcon name="fa-eraser" />
                  Clear
                </button>
              </div>
            </div>

          </div>
        </section>

        {/* ===================================
            BRANCH SUMMARY
        =================================== */}

        {showBranchSummary && (
          <section className="rcm-panel">
            <div className="rcm-panel-header">
              <div className="rcm-panel-heading">
                <div className="rcm-panel-heading-icon">
                  <ReportIcon name="fa-code-branch" />
                </div>

                <div>
                  <h2 className="rcm-panel-title">
                    Network Branch Summary
                  </h2>
                  <div className="rcm-panel-description">
                    Team investment distribution by branch
                  </div>
                </div>
              </div>
            </div>

            <div className="rcm-panel-body">
              <div className="rcm-branch-grid">
                {branches.map((branch) => (
                  <div
                    className="rcm-branch-card"
                    key={branch.title}
                  >
                    <div className="rcm-branch-name">
                      <ReportIcon name={branch.icon} />
                      {branch.title}
                    </div>

                    <div className="rcm-branch-amount">
                      {currency}
                      {formatMoney(branch.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ===================================
            TEAM INVESTMENT TABLE
        =================================== */}

        <section className="rcm-panel">
          <div className="rcm-panel-header">
            <div className="rcm-panel-heading">
              <div className="rcm-panel-heading-icon">
                <ReportIcon name="fa-table-list" />
              </div>

              <div>
                <h2 className="rcm-panel-title">
                  Team Statement List
                </h2>

                <div className="rcm-panel-description">
                  Detailed investment transaction records
                </div>
              </div>
            </div>

            <div className="rcm-table-meta">
              {refreshing && (
                <span className="rcm-refreshing">
                  <ReportIcon name="fa-arrows-rotate fa-spin" />
                  Updating
                </span>
              )}

              <span className="rcm-record-count">
                <ReportIcon name="fa-list-check" />
                {filteredData.length} Records
              </span>
            </div>
          </div>

          <div className="rcm-table-scroll">
            <table className="rcm-table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Username</th>
                  <th>Email Address</th>
                  <th>Investment Amount</th>
                  <th>Site</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>

              <tbody>
                {loading && investData.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="rcm-empty">
                        <div className="rcm-empty-icon">
                          <ReportIcon name="fa-arrows-rotate fa-spin" />
                        </div>

                        <div className="rcm-empty-title">
                          Loading Report
                        </div>

                        <div className="rcm-empty-desc">
                          Retrieving investment records...
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => {
                    const statusInfo = getStatusInfo(row.status);

                    const serial =
                      (currentPage - 1) * pageSize + index + 1;

                    return (
                      <tr
                        key={
                          row.id ??
                          `${row.username}-${row.create_date}-${serial}`
                        }
                      >
                        <td>{serial}</td>

                        <td>
                          <div className="rcm-username">
                            <span className="rcm-avatar">
                              {String(row.username || "U")
                                .charAt(0)
                                .toUpperCase()}
                            </span>

                            <span>{row.username || "N/A"}</span>
                          </div>
                        </td>

                        <td>{row.email || "N/A"}</td>

                        <td>
                          <span className="rcm-amount">
                            {currency}
                            {formatMoney(row.fixt_amount)}
                          </span>
                        </td>

                        <td>
                          <span className="rcm-site">
                            {String(
                              row.team_position || "-"
                            ).toUpperCase()}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`rcm-badge rcm-badge-${statusInfo.type}`}
                          >
                            <ReportIcon name={statusInfo.icon} />
                            {statusInfo.text}
                          </span>
                        </td>

                        <td>{row.create_date || "-"}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="rcm-empty">
                        <div className="rcm-empty-icon">
                          <ReportIcon name="fa-folder-open" />
                        </div>

                        <div className="rcm-empty-title">
                          No Records Found
                        </div>

                        <div className="rcm-empty-desc">
                          No investment records match your filters.
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}

          <div className="rcm-pagination">
            <div className="rcm-pagination-info">
              Showing{" "}
              {filteredData.length
                ? (currentPage - 1) * pageSize + 1
                : 0}
              {" - "}
              {Math.min(
                currentPage * pageSize,
                filteredData.length
              )}
              {" of "}
              {filteredData.length} records
            </div>

            <div className="rcm-pagination-controls">
              <select
                className="rcm-select rcm-page-size"
                value={pageSize}
                aria-label="Rows per page"
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <button
                type="button"
                className="rcm-page-btn"
                disabled={currentPage <= 1}
                onClick={() =>
                  setPage((prev) => Math.max(1, prev - 1))
                }
                aria-label="Previous page"
              >
                <ReportIcon name="fa-chevron-left" />
              </button>

              <span className="rcm-page-current">
                {currentPage} / {totalPages}
              </span>

              <button
                type="button"
                className="rcm-page-btn"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setPage((prev) =>
                    Math.min(totalPages, prev + 1)
                  )
                }
                aria-label="Next page"
              >
                <ReportIcon name="fa-chevron-right" />
              </button>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default TeamInvestReport;
